import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Share2, 
  Plus, 
  Search,
  Edit,
  Trash2,
  Calendar,
  Heart,
  MessageCircle,
  Repeat2,
  TrendingUp,
  Facebook,
  Instagram,
  Linkedin,
  Twitter
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { marketingAPI, SocialCampaign } from '../../lib/marketing-api';
import { toast } from 'sonner';
import { logger } from '../../services/logger.service';

export function SocialCampaigns() {
  const [campaigns, setCampaigns] = useState<SocialCampaign[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<SocialCampaign | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    content: '',
    platforms: [] as string[],
    status: 'draft' as SocialCampaign['status'],
    scheduledDate: '',
    propertyId: ''
  });

  const platformOptions = [
    { id: 'facebook', name: 'Facebook', icon: Facebook },
    { id: 'instagram', name: 'Instagram', icon: Instagram },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin },
    { id: 'twitter', name: 'Twitter/X', icon: Twitter }
  ];

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setIsLoading(true);
      const { campaigns: data } = await marketingAPI.getSocialCampaigns();
      setCampaigns(data);
    } catch (error) {
      logger.error('Failed to load social campaigns', error as Error, undefined, 'SocialCampaigns');
      toast.error('Failed to load social campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      await marketingAPI.createSocialCampaign(formData);
      toast.success('Social campaign created successfully');
      setShowAddDialog(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to create campaign', error as Error, undefined, 'SocialCampaigns');
      toast.error('Failed to create campaign');
    }
  };

  const handleUpdateCampaign = async () => {
    if (!editingCampaign) return;
    
    try {
      await marketingAPI.updateSocialCampaign(editingCampaign.id, formData);
      toast.success('Campaign updated successfully');
      setEditingCampaign(null);
      resetForm();
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to update campaign', error as Error, undefined, 'SocialCampaigns');
      toast.error('Failed to update campaign');
    }
  };

  const handlePublishCampaign = async (campaign: SocialCampaign) => {
    if (!confirm(`Publish "${campaign.name}" to ${campaign.platforms.join(', ')}?`)) return;
    
    try {
      // Simulate publishing with random engagement metrics
      const randomLikes = Math.floor(Math.random() * 500) + 50;
      const randomShares = Math.floor(Math.random() * 100) + 10;
      const randomComments = Math.floor(Math.random() * 50) + 5;
      const randomReach = Math.floor(Math.random() * 5000) + 1000;
      
      await marketingAPI.updateSocialCampaign(campaign.id, {
        status: 'published',
        engagement: {
          likes: randomLikes,
          shares: randomShares,
          comments: randomComments,
          reach: randomReach
        }
      });
      toast.success('Campaign published successfully!');
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to publish campaign', error as Error, undefined, 'SocialCampaigns');
      toast.error('Failed to publish campaign');
    }
  };

  const handleDeleteCampaign = async (campaign: SocialCampaign) => {
    if (!confirm(`Delete campaign "${campaign.name}"? This action cannot be undone.`)) return;

    try {
      await marketingAPI.deleteSocialCampaign(campaign.id);
      toast.success('Campaign deleted successfully');
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to delete campaign', error as Error, undefined, 'SocialCampaigns');
      toast.error('Failed to delete campaign');
    }
  };

  const openEditDialog = (campaign: SocialCampaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      content: campaign.content,
      platforms: campaign.platforms,
      status: campaign.status,
      scheduledDate: campaign.scheduledDate || '',
      propertyId: campaign.propertyId || ''
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      content: '',
      platforms: [],
      status: 'draft',
      scheduledDate: '',
      propertyId: ''
    });
  };

  const togglePlatform = (platformId: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const getStatusBadge = (status: SocialCampaign['status']) => {
    const variants: Record<SocialCampaign['status'], string> = {
      draft: 'bg-gray-500',
      scheduled: 'bg-blue-500',
      published: 'bg-green-500',
      completed: 'bg-purple-500'
    };
    
    return (
      <Badge className={variants[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, any> = {
      facebook: Facebook,
      instagram: Instagram,
      linkedin: Linkedin,
      twitter: Twitter
    };
    const Icon = icons[platform] || Share2;
    return <Icon className="w-4 h-4" />;
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    (campaign.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (campaign.content || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalReach = campaigns.reduce((sum, c) => sum + (c.engagement?.reach || 0), 0);
  const totalEngagement = campaigns.reduce((sum, c) => 
    sum + (c.engagement?.likes || 0) + (c.engagement?.shares || 0) + (c.engagement?.comments || 0), 0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Share2 className="w-6 h-6 text-primary" />
            Social Media Campaigns
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage multi-platform social media campaigns
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="marketing-social-new-button">
          <Plus className="w-4 h-4" />
          New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
                <p className="text-2xl font-bold">{campaigns.length}</p>
              </div>
              <Share2 className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Reach</p>
                <p className="text-2xl font-bold">{totalReach.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Engagement</p>
                <p className="text-2xl font-bold">{totalEngagement.toLocaleString()}</p>
              </div>
              <Heart className="w-8 h-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Campaigns</p>
                <p className="text-2xl font-bold">
                  {campaigns.filter(c => c.status === 'published').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <CardTitle>All Campaigns ({filteredCampaigns.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              Loading campaigns...
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No campaigns found. Create your first social media campaign!
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.map((campaign) => (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{campaign.name}</h3>
                        {getStatusBadge(campaign.status)}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {campaign.content}
                      </p>
                      <div className="flex items-center gap-2 mb-3">
                        {campaign.platforms.map(platform => (
                          <Badge key={platform} variant="outline" className="gap-1">
                            {getPlatformIcon(platform)}
                            {platform}
                          </Badge>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <TrendingUp className="w-4 h-4" />
                            <span>Reach</span>
                          </div>
                          <p className="font-semibold">
                            {(campaign.engagement?.reach || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Heart className="w-4 h-4" />
                            <span>Likes</span>
                          </div>
                          <p className="font-semibold text-red-600">
                            {(campaign.engagement?.likes || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Repeat2 className="w-4 h-4" />
                            <span>Shares</span>
                          </div>
                          <p className="font-semibold text-blue-600">
                            {(campaign.engagement?.shares || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <MessageCircle className="w-4 h-4" />
                            <span>Comments</span>
                          </div>
                          <p className="font-semibold text-green-600">
                            {(campaign.engagement?.comments || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span>Created</span>
                          </div>
                          <p className="font-semibold">
                            {new Date(campaign.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(campaign)}
                        title="Edit"
                        data-testid="marketing-social-edit-button"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {campaign.status === 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePublishCampaign(campaign)}
                          className="text-green-600 hover:bg-green-500/10"
                          title="Publish"
                          data-testid="marketing-social-publish-button"
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteCampaign(campaign)}
                        className="text-destructive hover:bg-destructive/10"
                        title="Delete"
                        data-testid="marketing-social-delete-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Campaign Dialog */}
      <Dialog open={showAddDialog || editingCampaign !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setEditingCampaign(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create Social Media Campaign'}
            </DialogTitle>
            <DialogDescription>
              {editingCampaign ? 'Update campaign details' : 'Design a new social media campaign'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Campaign Name *</Label>
              <Input
                id="campaign-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Luxury Apartment Showcase"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Post Content *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your social media post here..."
                rows={6}
                required
              />
              <p className="text-xs text-muted-foreground">
                {formData.content.length} characters
              </p>
            </div>
            <div className="space-y-2">
              <Label>Platforms *</Label>
              <div className="grid grid-cols-2 gap-3">
                {platformOptions.map(platform => (
                  <div key={platform.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={platform.id}
                      checked={formData.platforms.includes(platform.id)}
                      onCheckedChange={() => togglePlatform(platform.id)}
                    />
                    <label
                      htmlFor={platform.id}
                      className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      <platform.icon className="w-4 h-4" />
                      {platform.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: SocialCampaign['status']) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="scheduledDate">Schedule Date (Optional)</Label>
                <Input
                  id="scheduledDate"
                  type="datetime-local"
                  value={formData.scheduledDate}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyId">Link to Property (Optional)</Label>
              <Input
                id="propertyId"
                value={formData.propertyId}
                onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
                placeholder="Property ID or reference"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setEditingCampaign(null);
                resetForm();
              }}
              data-testid="marketing-social-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
              disabled={formData.platforms.length === 0}
              data-testid="marketing-social-save-button"
            >
              {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
